import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { withTranslation, withUserPreferences, withAccount } from 'components/hoc';
import styled from 'styled-components';
import { AuthGatedTheme } from '../common/AuthGatedTheme';
import './index.css';

const DisclaimerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 56px 32px 96px;
  box-sizing: border-box;

  @media (max-width: 992px) {
    padding: 48px 24px 80px;
  }

  @media (max-width: 768px) {
    padding: 36px 18px 72px;
  }

  @media (max-width: 540px) {
    padding: 28px 16px 60px;
  }
`;

const Section = styled.section`
  margin-top: 1.5rem;
  line-height: 1.7;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }
`;

const DisclaimerContent = ({ isAuth, translate }) => {
  const textColor = isAuth ? { color: 'var(--admincat-color-grey-2)' } : {};
  const bgColor = isAuth ? { backgroundColor: 'var(--admincat-color-grey-4)' } : {};

  const getMarginLeft = (text) => {
    const length = text.length;
    if (length === 5) return '48px';
    if (length === 6) return '49px';
    if (length === 8) return '38px';
    return '41px';
  };

  return (
    <div className="credits-disclaimer" style={bgColor}>
      <div className="credits">
        <div>
          <h1 style={textColor}>{translate('Credits')}</h1>
          
          <Section>
            <div className="general-contact">
              <div className="address-container">
                <div className="address" style={textColor}>
                  <h3 style={textColor}>{translate('Head office')}:</h3>
                  <p className="contact-address" style={textColor}>
                    AdminCat GmbH <br />
                    Landgraf-Karl-Str. 21A <br />
                    34131 Kassel <br />
                    <span>{translate('Germany')}</span>
                  </p>
                </div>
              </div>
            </div>
          </Section>

          <Section>
            <div className="contact-info">
              <p style={textColor}>
                <span>{translate('Phone')}:</span>
                <span
                  style={{ display: 'inline-block', marginLeft: getMarginLeft(translate('Phone')) }}
                ></span>
                +49 561 201 74392 ({translate('no support')})
              </p>
              <p style={textColor}>
                <span>{translate('E-Mail')}:</span>
                <span
                  style={{ display: 'inline-block', marginLeft: getMarginLeft(translate('E-Mail')) }}
                ></span>
                <a style={{ fontSize: '18px' }} href="mailto:admin@admincat.net">
                  admin@admincat.net
                </a>
              </p>
            </div>
          </Section>

          <Section>
            <p style={textColor}>
              ----------------------------------------------------------------------------------------------
            </p>
            <p className="line" style={textColor}>
              <span>{translate('Support')}:</span>
              <span
                style={{ display: 'inline-block', marginLeft: getMarginLeft(translate('Support:')) }}
              ></span>
              <a style={{ fontSize: '18px' }} href="mailto:support@admincat.net">
                support@admincat.net
              </a>
            </p>
            <p style={textColor}>
              ----------------------------------------------------------------------------------------------
            </p>
          </Section>

          <Section>
            <p style={textColor}>
              <span>{translate('Authorized managing director')}</span>: Benjamin Kluge<br />
              <span>{translate('Register court')}</span>: <span>{translate('Local court ')} </span>Kassel <br />
              <span>{translate('Register number')}</span>: HRB 19706 <br />
              <span>{translate('VAT ID')}</span>: DE366542355 <br />
            </p>
          </Section>
        </div>
      </div>

      <div className="disclaimer">
        <h1 style={textColor}>{translate('Disclaimer')}</h1>

        <Section>
          <h3 style={textColor}>{translate('Liability for Content')}</h3>
          <p style={textColor}>
            {translate(
              'The content provided has been created with greatest care. However we cannot guarantee for the accuracy, completeness, reliability, usability and timeliness of the content. As a service provider according to § 7 (1) of the German Telemedia Act (TMG), we are responsible for own content on these sites under the general laws. However, corresponding to §§ 8 to 10 TMG, it is not our duty, to control or check information transmitted or saved from third parties, whether or not they are in line with the law. Obligations to remove or block the use of information under the general laws remain unaffected. However, liability is only possible from the date of knowledge of a specific infringement. Upon notification of such violations, we will remove the content immediately.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>{translate('Copyrights')}</h3>
          <p style={textColor}>
            {translate(
              'The contents and works on these pages compiled by us are subject to copyright law. Copying, processing, distribution and any kind of use outside the limits of copyright law require the written consent of us. In case the content is not created by us, the copyrights of third parties are being observed. In particular contents of third parties are marked as such. However, if a user becomes aware of a copyright infringement, we ask the user for notification. Upon notification of such violations, we will remove the content immediately.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>{translate('Data Protection')}</h3>
          <p style={textColor}>
            {translate(
              'The collection of person-related data on our website is based, as far as possible, on voluntariness. These data will not be transmitted to third parties without your explicit approval. Please be aware, that the data transmission over the internet (e.g. communication via email) may bear security risks. Therefore a complete protection of data from access of third parties is not possible.',
            )}
            <br /><br />
            {translate(
              'We hereby expressly object to the use of contact data published within the framework of the imprint obligation by third parties for sending unsolicited advertising and information material. The site operators reserve the right to take legal action in the event that unsolicited advertising information is sent, such as spam e-mails.',
            )}
          </p>
        </Section>
      </div>
    </div>
  );
};

const Disclaimer = (props) => {
  const { tr, account: { user: { isAuth } } } = props;
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  // Use tr for authenticated users, t for non-authenticated users
  const translate = isAuth ? tr : t;

  return (
    <AuthGatedTheme isAuth={isAuth}>
      <DisclaimerWrapper>
        <DisclaimerContent isAuth={isAuth} translate={translate} />
      </DisclaimerWrapper>
    </AuthGatedTheme>
  );
};

export default compose(withTranslation, withUserPreferences, withAccount)(Disclaimer);